import styled from '@emotion/styled';
import { AnimatePresence } from 'framer-motion';
import { useState, useEffect, useMemo } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';

import { Layout } from '../../Layout';
import { RouteManager } from '../../RouteManager';

import { HomeyUtils } from '../../store/HomeyUtils';

import { useEventCallback } from '../../hooks/useEventCallback';
import { useI18n } from '../../hooks/useI18nFormatters';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { useMount } from '../../hooks/useMount';
import { useQuery } from '../../hooks/useQuery';
import { useLayoutContextState } from '../../LayoutContext';
import { useAdvancedFlowsAttach } from '../../store/advanced-flow/useAdvancedFlows.ts';
import {
  useDevicesData,
  useDevicesAttach,
  useDevicesById,
} from '../../store/devices/useDevices.ts';
import { useFlowsAttach } from '../../store/flow/useFlows.ts';
import { useLogsAttach } from '../../store/insights/useLogs.ts';
import { useMoodsAttach } from '../../store/moods/useMoods.ts';
import { useApi } from '../../store/SelectedHomeyStore.hooks.ts';
import { useWebAppSettings } from '../../store/web-app-settings/useWebAppSettings';
import { useZonesAttach, useZonesTree } from '../../store/zones/useZones.ts';

import { publicUrl } from '../../theme/functions/publicUrl';
import { su } from '../../theme/functions/su';
import { glyph } from '../../theme/glyph';
import { theme } from '../../theme/theme';

import { GradientButton } from '../../components/buttons/GradientButton';
import { Modus, ModusButton } from '../../components/common/Modus';
import { Scroll } from '../../components/common/Scroll';
import { Heading2Bold } from '../../components/common/text/Heading2Bold';
import { TextRegular } from '../../components/common/text/TextRegular';
import { Controls } from '../../components/controls/Controls';
import { DeviceTile } from '../../components/device/DeviceTile';
import { PairingDelegator } from '../../components/pairing/PairingDelegator';
import { PremiumRequiredDialog } from '../../components/premium/PremiumRequiredDialog';

import { DevicesAddMenu } from './DevicesAddMenu';
import { DevicesMoreMenu } from './DevicesMoreMenu';
import { HiddenDevicesDialog } from './HiddenDevicesDialog';
import { HiddenDevicesFooter } from './HiddenDevicesFooter';
import { DeviceSettingsDialog } from './settings/DeviceSettingsDialog';
import { TableView } from './table/TableView';
import { ZoneNavigation } from './zone-navigation/ZoneNavigation';
import { ZonesView } from './ZonesView';

export function DevicesPage() {
  const { i18n } = useI18n();
  const location = useLocation();
  const mount = useMount();

  const { api, homey } = useApi();

  useDevicesAttach();
  useZonesAttach();
  useMoodsAttach();
  useLogsAttach();
  useFlowsAttach();
  useAdvancedFlowsAttach();

  const { layoutContextState } = useLayoutContextState();

  const { settings } = useWebAppSettings();

  const devices = useDevicesData();
  const devicesById = useDevicesById();
  const zones = useZonesTree();
  const deviceMatch = useRouteMatch(`${import.meta.env.BASE_URL}homeys/:homeyId/devices/:deviceId`);
  const deviceId = deviceMatch?.params?.deviceId || null;

  const params = useQuery();
  const dialogType = params.get('dialog');
  const dialogResourceKey = params.get('key');

  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState(deviceId);
  const [tileSize, setTileSize] = useLocalStorageState(DeviceTile.size.medium, 'deviceTileSize');
  const [isHiddenDevicesDialogOpen, setIsHiddenDevicesDialogOpen] = useState(false);
  const [draftZone, setDraftZone] = useState(null);

  function handleNewZone() {
    const rootId = zones.tree?.root?.id;

    if (rootId == null) {
      return;
    }

    setDraftZone({ id: 'create', parentId: rootId });
  }

  const supportsHiddenDevices = HomeyUtils.getHasHiddenDevices(homey);

  const { hiddenDeviceCount, hasDevices } = useMemo(() => {
    if (!devicesById.byId) return { hiddenDeviceCount: 0, hasDevices: false };

    let hiddenDeviceCount = 0;
    let hasDevices = false;

    for (const device of Object.values(devicesById.byId)) {
      if (device.group != null) continue;

      if (selectedZoneId != null) {
        const descendants = zones.tree?.descendants?.[selectedZoneId] ?? new Set();

        if (device.zone !== selectedZoneId && !descendants.has(device.zone)) {
          continue;
        }
      }

      hasDevices = true;

      if (supportsHiddenDevices && device.hidden === true) {
        hiddenDeviceCount++;
      }
    }

    return { hiddenDeviceCount, hasDevices };
  }, [devicesById.byId, selectedZoneId, zones.tree, supportsHiddenDevices]);

  useEffect(() => {
    return function () {
      if (mount.isMounted) {
        setSelectedZoneId(null);
        setSelectedDeviceId(null);
      }
    };
  }, [api, mount]);

  useEffect(() => {
    setSelectedDeviceId(deviceId);
  }, [deviceId]);

  const handleControlsClick = useEventCallback((event, device) => {
    if (selectedDeviceId === device.id) {
      RouteManager.toDevices();
      return;
    }

    RouteManager.toDevice(device.id);
  });

  function onModusPress(value) {
    setTileSize(value);
  }

  return (
    <Layout
      isRightOpen={selectedDeviceId != null}
      subNavigationProps={{
        key: 'devices',
        width: 280,
        title: i18n.messageFormatter('navigation.devices'),
        titleAction: <DevicesAddMenu onNewZone={handleNewZone} />,
      }}
      subNavigationChildren={
        <ZoneNavigation
          selectedZoneId={selectedZoneId}
          setSelectedZoneId={setSelectedZoneId}
          draftZone={draftZone}
          setDraftZone={setDraftZone}
        />
      }
      actions={
        <S.Actions>
          <Modus>
            <ModusButton
              onPress={() => onModusPress(null)}
              isActive={tileSize === null}
              url={glyph.core.rowGrid}
            />
            <ModusButton
              onPress={() => onModusPress(DeviceTile.size.medium)}
              isActive={tileSize === DeviceTile.size.medium}
              url={glyph.core.squareGrid}
            />
            <ModusButton
              onPress={() => onModusPress(DeviceTile.size.large)}
              isActive={tileSize === DeviceTile.size.large}
              url={glyph.core.squares}
            />
          </Modus>
          {supportsHiddenDevices && (
            <DevicesMoreMenu
              hasDevices={hasDevices}
              onHiddenDevicesPress={() => setIsHiddenDevicesDialogOpen(true)}
            />
          )}
        </S.Actions>
      }
      content={(() => {
        if (devices.isLoading === false && Object.keys(devices.data ?? {}).length === 0) {
          return (
            <Scroll flex={true}>
              <S.Root>
                <S.Content>
                  {settings.theme.darkMode === true ? (
                    <S.Image
                      height="480"
                      srcSet={`${publicUrl('img/devices/empty-dark.png')} 1x, ${publicUrl(
                        'img/devices/empty-dark@2x.png',
                      )} 2x`}
                      src={publicUrl('img/devices/empty-dark.png')}
                    />
                  ) : (
                    <S.Image
                      height="480"
                      srcSet={`${publicUrl('img/devices/empty-light.png')} 1x, ${publicUrl(
                        'img/devices/empty-light@2x.png',
                      )} 2x`}
                      src={publicUrl('img/devices/empty-light.png')}
                    />
                  )}
                  <S.Title>{i18n.messageFormatter('devices.emptyDevicesTitle')}</S.Title>
                  <S.Subtitle color={theme.color.text_body_light}>
                    {i18n.messageFormatter('devices.emptyDevicesSubtitle')}
                  </S.Subtitle>
                  <GradientButton
                    onPress={() => {
                      RouteManager.toNewDevice();
                    }}
                  >
                    {i18n.messageFormatter('devices.connectDevice')}
                  </GradientButton>
                </S.Content>
              </S.Root>
            </Scroll>
          );
        }
        const footerComponent = supportsHiddenDevices ? (
          <HiddenDevicesFooter
            hiddenCount={hiddenDeviceCount}
            onCustomizePress={() => setIsHiddenDevicesDialogOpen(true)}
          />
        ) : null;

        if (tileSize == null) {
          return (
            <TableView
              selectedZoneId={selectedZoneId}
              selectedDeviceId={selectedDeviceId}
              onControlsClick={handleControlsClick}
              footerComponent={footerComponent}
            />
          );
        }

        return (
          <Scroll
            flex={true}
            style={{
              '--scrollbar-thumb-background': layoutContextState.scrollbarThumbBackgroundMV,
            }}
          >
            <S.Root>
              <ZonesView
                selectedZoneId={selectedZoneId}
                selectedDeviceId={selectedDeviceId}
                tileSize={tileSize}
                onControlsClick={handleControlsClick}
                footerComponent={footerComponent}
              />
            </S.Root>
          </Scroll>
        );
      })()}
      right={<Controls deviceId={selectedDeviceId} onClosePress={() => RouteManager.toDevices()} />}
    >
      <AnimatePresence>
        {dialogType === RouteManager.dialogRoute.deviceSettings && (
          <DeviceSettingsDialog deviceId={dialogResourceKey} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {location.state?.pairDialog != null && (
          <PairingDelegator state={location.state.pairDialog} zoneId={selectedZoneId} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {location.state?.premiumRequiredDialog === true && <PremiumRequiredDialog />}
      </AnimatePresence>

      <AnimatePresence>
        {isHiddenDevicesDialogOpen && (
          <HiddenDevicesDialog
            selectedZoneId={selectedZoneId}
            onClose={() => setIsHiddenDevicesDialogOpen(false)}
          />
        )}
      </AnimatePresence>
    </Layout>
  );
}

const S = (DevicesPage.S = class S {
  static Root = styled.section`
    display: flex;
    flex: 1 1 auto;
    padding: 0 ${su(4)} ${su(2)};
  `;

  static Content = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
  `;

  static Image = styled.img`
    display: block;
    max-height: 40vh;
  `;

  static Title = styled(Heading2Bold)`
    margin-bottom: ${su(1)};
  `;

  static Subtitle = styled(TextRegular)`
    margin-bottom: ${su(2)};
  `;

  static Actions = styled.div`
    display: flex;
    align-items: center;
    gap: ${su(2)};
  `;
});
